import { useState, useEffect, useMemo } from 'react';
import { Button, QRCode, Form, Input, message } from 'antd';
import classnames from 'classnames/bind';
import { AntdGarbageDetection } from '@yaotai/components';
import { YAOTAI_ICO_PNG, useAntMessage } from 'common-package';
import { BUSINESS_TYPE, GarbageDetectionForm } from '@commonPackage/constants';
import { CountryPhoneFieldFormItem } from '@commonPackage/components';
import { OPEN_RECOMMEND_SHARE } from 'portal-common/src/constants';
import { checkInternationalPhone, checkInternationalPhoneRequired, trimStr } from '@portalCommon/utils';
import { useGenerateCodeLink } from '@portalCommon/hooks';
import { ReferralInvalidToast } from '@portalCommon/components';
import API from '@/services';
import { BaseModal, ModalCommonType } from '.';
import styles from './index.module.less';

const cx = classnames.bind(styles);

type ShareComponentProps = {
  code: string;
  show: boolean;
};

// 分享链接与二维码邀请组件
const ShareCop = (props: ShareComponentProps & { codeLink: string }) => {
  const { code, codeLink, show } = props;
  const [textList, setTextList] = useState<string[]>([]);
  const { copy, messageContextHolder } = useAntMessage();

  useEffect(() => {
    handleSpeechList();
  }, []);
  const handleSpeechList = async () => {
    const { data } = await API.recommend.getSpeechList();
    const list = data.map(item => item.content.replace('{链接}', codeLink).replace('{推荐码}', code));
    setTextList(list);
  };
  const qrCodeUrl = useMemo(() => {
    const winLocation = window.location;
    const params = new URLSearchParams({ [OPEN_RECOMMEND_SHARE]: '1' });
    params.set('code', code);
    const link = winLocation.origin + winLocation.pathname + '?' + params.toString();

    return link;
  }, [code]);

  return (
    <div className={cx('share-link-box', show ? 'show' : 'hidden')}>
      <p className={cx(`share-code`)}>
        <span>我的推荐码</span>
        <i>{code}</i>
        <b onClick={() => copy(code as string, '复制成功')}>复制</b>
      </p>
      <div className={cx(`share-qrcode`)}>
        <QRCode errorLevel="H" size={140} iconSize={26} value={qrCodeUrl} icon={YAOTAI_ICO_PNG} />
      </div>
      <p className={cx(`share-tip`)}>扫码分享给我的好友</p>
      <div className={cx(`share-link`)}>
        <span>{codeLink}</span>
        <Button className={cx(`share-link-btn`)} onClick={() => copy(codeLink, '复制成功')}>
          复制专属链接
        </Button>
      </div>
      <div className={cx(`share-list`)}>
        <p>如何让推荐更有效？</p>
        <div className={cx('text-box')}>
          {textList.map((item, index) => (
            <div key={index} className={cx('text-item')}>
              <span>推荐语 {index + 1}</span>
              <div className={cx('text-item-content')}>
                <div>
                  <p>{item}</p>
                  <b onClick={() => copy(item, '复制成功')}>复制</b>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {messageContextHolder}
    </div>
  );
};

// 填资料邀请
const FillInCop = (props: ShareComponentProps) => {
  const { show, code } = props;
  const [fillSuccess, setFillSuccess] = useState(false);
  const [messageApi, messageContextHolder] = message.useMessage();
  const [form] = Form.useForm();

  const onFinish = async (value: Record<string, any>) => {
    const params = getBodyV3(value);
    const {
      data: { show_referrer_invalid_tip },
    } = await API.contact.contact(params);

    show_referrer_invalid_tip &&
      messageApi.warning(
        ReferralInvalidToast(`“推荐有奖计划”仅面向新用户，您推荐的好友已经是网易瑶台的%s啦～`, ['老朋友']),
      );
    setFillSuccess(true);
  };

  const getBodyV3 = (value: Record<string, any>) => {
    const { name, phone, orga_name, title } = value;

    return {
      source: 'activity_advisory',
      from_url: window.location.href,
      name: trimStr(name),
      phone: `+${phone?.code}${phone?.phone}`,
      orga_name: trimStr(orga_name),
      title: title ? title : undefined,
      referrer_code: code,
      discover: [['朋友推荐', code]],
    };
  };

  const againFillClick = () => {
    form.resetFields();
    setFillSuccess(false);
  };
  return (
    <div className={cx('share-fill', show ? 'show' : 'hidden')}>
      {fillSuccess ? (
        <div className={cx('share-again')}>
          <img src="https://static.fuxi.netease.com/yaotai/portal/index/li8lrkqk_815996.png" alt="" />
          <p>提交成功</p>
          <Button type="primary" onClick={againFillClick}>
            再次填写
          </Button>
        </div>
      ) : (
        <Form
          className={cx('fill-form-content')}
          form={form}
          labelCol={{ span: 6 }}
          labelAlign="right"
          onFinish={onFinish}>
          <AntdGarbageDetection
            form={form}
            request={(info: GarbageDetectionForm) => API.activity.getGarbageDetection({ ...info, need_detail: false })}
            name="name"
            type={BUSINESS_TYPE.TEXT}
            render={(validator, detectionEvent) => (
              <Form.Item
                label="姓名"
                name="name"
                rules={[{ validator }, { required: true, whitespace: true, max: 20 }]}>
                <Input
                  placeholder="请输入姓名"
                  onBlur={e => {
                    detectionEvent(e);
                  }}
                />
              </Form.Item>
            )}></AntdGarbageDetection>
          <CountryPhoneFieldFormItem
            labelAlign="right"
            labelName="手机号"
            form={form}
            validateTrigger={[]}
            placeholder="请输入手机号"
            rules={[
              {
                validator: checkInternationalPhoneRequired('请输入手机号'),
              },
              {
                validator: checkInternationalPhone(true, '请输入正确的手机号'),
              },
            ]}
          />

          <AntdGarbageDetection
            form={form}
            request={(info: GarbageDetectionForm) => API.activity.getGarbageDetection({ ...info, need_detail: false })}
            name="orga_name"
            type={BUSINESS_TYPE.TEXT}
            render={(validator, detectionEvent) => (
              <Form.Item
                label="组织/公司"
                name="orga_name"
                rules={[{ validator }, { required: true, whitespace: true, max: 50 }]}>
                <Input
                  placeholder="请输入组织/公司名称"
                  onBlur={e => {
                    detectionEvent(e);
                  }}
                />
              </Form.Item>
            )}></AntdGarbageDetection>
          <AntdGarbageDetection
            form={form}
            request={(info: GarbageDetectionForm) => API.activity.getGarbageDetection({ ...info, need_detail: false })}
            name="title"
            type={BUSINESS_TYPE.TEXT}
            render={(validator, detectionEvent) => (
              <Form.Item label="职位" name="title" rules={[{ validator }, { whitespace: true, max: 50 }]}>
                <Input
                  placeholder="请输入职位"
                  onBlur={e => {
                    detectionEvent(e);
                  }}
                />
              </Form.Item>
            )}
          />
          <Form.Item>
            <Button type="primary" htmlType="submit">
              提交
            </Button>
          </Form.Item>
        </Form>
      )}
      {messageContextHolder}
    </div>
  );
};

export function RecommendShareModal(props: ModalCommonType) {
  const { onClose, width } = props;

  const [activeIndex, setActiveIndex] = useState(0);
  const [code, codeLink] = useGenerateCodeLink();

  const tabClick = (i: number) => {
    setActiveIndex(i);
  };

  return (
    <BaseModal onClose={onClose} width={width} confirmClose className={cx('share-base-modal')}>
      <div className={cx('recommend-share')}>
        <p className={cx(`recommend-share-title`)}>选择方式开始推荐</p>
        <div className={cx('recommend-share-tabs')}>
          {['直接填写好友资料', '分享推荐链接'].map((item, index) => (
            <span
              key={item + index}
              onClick={tabClick.bind(null, index)}
              className={cx({ active: index === activeIndex })}>
              {item}
            </span>
          ))}
        </div>
        <FillInCop show={!activeIndex} code={code as string} />
        <ShareCop show={!!activeIndex} codeLink={codeLink} code={code as string} />
      </div>
    </BaseModal>
  );
}
